import AntSwitch from '@/components/form-control/AntSwitch'
import InputLabelField from '@/components/form-control/InputLabelField'
import SelectField from '@/features/admin/components/common/SelectField'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import useGetDetailSensor from '@/features/config/hooks/hydraulic/flowSensor/useGetDetailSensor'
import useAddFlowValve from '@/features/config/hooks/hydraulic/flowValve/useAddFlowValve'
import useEditFlowValve from '@/features/config/hooks/hydraulic/flowValve/useEditFlowValve'
import useGetDetailInput from '@/features/config/hooks/hydraulic/flowValve/useGetDetailInput'
import useSearchFlowValveId from '@/features/config/hooks/hydraulic/flowValve/useSearchFlowValveId'
import { useUnits } from '@/hooks/useUnits'
import { FlowValveType, FormFlowValve, ValveForm } from '@/types/hydraulic'
import {
  decoderLinkFields,
  storedDecoderOrUndefined,
  withDecoderId,
} from '@/utils/decoderLink'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Container, Dialog, Stack, Typography } from '@mui/material'
import ConfirmModalUI from '@/components/common/ConfirmModalUI/ConfirmModalUI'
import useSidebarCloseGuard from '@/features/config/hooks/useSidebarCloseGuard'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { schema, typeList } from './constant'
import classes from './style.module.scss'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import { OutputDecoderType } from '@/types/decoder'
import { DATA_PAGINATION } from '@/types'
import flowValveApi from '@/api/config/flowValveApi'
import { NotifyService } from '@/helpers'
import Select from '@/components/form-control/Select'
import flowSensorApi from '@/api/config/flowSensorApi'

interface ValveProps {
  id?: number
  flowRuleId: number
  initialData?: FlowValveType
}

const FormValveView = (props: ValveProps) => {
  const { t } = useTranslation()
  const { id, flowRuleId, initialData } = props
  const { convertLabelUnit, convertFromMetricV2, convertToMetricV2 } =
    useUnits()

  const { closeSidebar } = useConfigUI()
  /**
   * @todo:
   * Fix schema validation,
   * For now cast to any
   */
  const form = useForm<ValveForm>({
    resolver: yupResolver(schema as any),
  })

  // SCRUM-1032 QC item 6 — backdrop and Escape no longer discard this form, and
  // Cancel asks before throwing away edits.
  const { requestClose, confirmClose, cancelClose, isConfirmOpen } =
    useSidebarCloseGuard(form.formState.isDirty)
  const { interfaceId } = useAuthContext()

  const mode = id ? 'edit' : 'create'

  //add
  const { data } = useGetDetailInput(Number(interfaceId), [
    OutputDecoderType.PUMP_START,
    OutputDecoderType.MV_NORMALLY_OPEN,
    OutputDecoderType.MV_NORMALLY_CLOSED,
    OutputDecoderType.BOOSTER_PUMP,
  ])
  const { data: dataSensorOption } = useGetDetailSensor(Number(interfaceId))
  const { mutate: addValve, isLoading: loadingAddValve } = useAddFlowValve()

  //edit
  const { data: dataItem } = useSearchFlowValveId(
    Number(interfaceId),
    Number(id),
  )
  const { isViewOnly } = usePermission()

  // dataSelect
  const dataSelect = useMemo(() => {
    const listClone =
      data?.map((item) => ({
        label: item.name,
        value: `${item.address}-${item.index}`,
      })) || []

    if (dataItem)
      listClone.unshift({
        label: dataItem.io,
        value: `${dataItem.address}-${dataItem.index}`,
      })
    return listClone
  }, [data, dataItem])

  const getOptions =
    () => async (PageIndex?: number, PageSize?: number, Keyword?: string) => {
      let data: DATA_PAGINATION | undefined
      let Purpose = [
        OutputDecoderType.PUMP_START,
        OutputDecoderType.MV_NORMALLY_OPEN,
        OutputDecoderType.MV_NORMALLY_CLOSED,
        OutputDecoderType.BOOSTER_PUMP,
      ]
      data = await flowValveApi
        .getDetailIO(Number(interfaceId), Purpose)
        .then((res) => {
          const dataList =
            res.data?.map((item) => ({
              ...item,
              label: item.name,
              value: `${item.address}-${item.index}-${item.twiId}`,
            })) || []

          return {
            data: dataList,
            page: 1,
            size: dataList.length,
            totalCount: dataList.length,
            totalPage: 1,
          }
        })
        .catch((e) => {
          NotifyService.error(e)
          return undefined
        })

      return data
    }
  const getFlowSensorOptions =
    () => async (PageIndex?: number, PageSize?: number, Keyword?: string) => {
      let data: DATA_PAGINATION | undefined
      data = await flowSensorApi
        .getDetails(Number(interfaceId))
        .then((res) => {
          const dataList =
            res.data?.map((item) => ({
              ...item,
              label: item.name,
              value: `${item.address}-${item.index}-${item.twiId}`,
            })) || []

          return {
            data: dataList,
            page: 1,
            size: dataList.length,
            totalCount: dataList.length,
            totalPage: 1,
          }
        })
        .catch((e) => {
          NotifyService.error(e)
          return undefined
        })

      return data
    }
  const { mutate: editFlowValve, isLoading: loadingEditValve } =
    useEditFlowValve()

  const populateForm = (source: FlowValveType) => {
    const convertedCapacity = convertFromMetricV2(
      source.capacity,
      'l/m',
      'gpm',
      2,
    )
    form.reset({
      addressValve: `${source.address}-${source.index}`,
      type: source.type,
      capacity: convertedCapacity,
      startDelay: source.startDelay,
      stopDelay: source.stopDelay,
      priority: source.priority,
      active: source.active,
      address: storedDecoderOrUndefined({
        id: source.decoderId,
        label: source.io,
        value: `${source.address}-${source.index}-${source.twiId}`,
        name: source.io,
        address: source.address,
        index: source.index,
        twiId: source.twiId,
        twiCustomName: source.twiCustomName,
      }),
      flowSensorId: source.flowSensorId
        ? {
            id: source.flowSensorId,
            label: source.flowSensorName,
            value: `${source.flowSensorAddress}-${source.flowSensorIndex}-${source.twiId}`,
            name: source.flowSensorName,
            address: source.flowSensorAddress,
            index: source.flowSensorIndex,
            twiCustomName: source.flowSensorTwiCustomName,
          }
        : undefined,
    })
  }

  useEffect(() => {
    if (dataItem) {
      populateForm(dataItem)
    } else if (mode === 'edit' && initialData) {
      populateForm(initialData)
    } else {
      form.reset({
        active: true,
        capacity: 0,
        priority: 0,
        startDelay: 0,
        stopDelay: 0,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataItem, initialData, form])

  const handleSave = (values: ValveForm) => {
    if (isViewOnly) return

    const capacityMetric = convertToMetricV2(values.capacity, 'l/m', 'gpm', 2)

    const valueCustom: FormFlowValve = {
      id: id ? Number(id) : 0,
      interfaceId: Number(interfaceId),
      active: values.active,
      type: values.type,
      priority: values.priority,
      ...decoderLinkFields(withDecoderId(values.address, data)),
      flowRuleId: flowRuleId,
      flowSensorId: values.flowSensorId?.id || 0,
      startDelay: values.startDelay,
      stopDelay: values.stopDelay,
      capacity: capacityMetric,
    }

    mode === 'create' ? addValve(valueCustom) : editFlowValve(valueCustom)
  }

  return (
    <Container sx={{ maxWidth: '450px' }}>
      <form
        onSubmit={form.handleSubmit(handleSave, (errors) => {
          console.log(
            '[FlowValve] validation FAILED - errors:',
            JSON.stringify(errors, null, 2),
          )
          console.log(
            '[FlowValve] current form values:',
            JSON.stringify(form.getValues(), null, 2),
          )
        })}
      >
        <Stack sx={{ padding: '34px 18px' }}>
          <Stack className={classes.valveHead}>
            <Typography className={classes.titleHead}>
              {id ? t('hydraulicEditFlowValve') : t('hydraulicAddFlowValve')}
            </Typography>
            <Stack direction="row">
              <Button
                variant="secondary"
                size="small"
                sx={{ marginRight: '7px', width: '85px' }}
                onClick={requestClose}
              >
                {t('commonActionsCancel')}
              </Button>
              <LoadingButton
                disabled={isViewOnly}
                size="small"
                loading={mode === 'create' ? loadingAddValve : loadingEditValve}
                variant={isViewOnly ? 'outlined' : 'primary'}
                sx={{ minWidth: '72px' }}
                type="submit"
              >
                {t('commonActionsSave')}
              </LoadingButton>
            </Stack>
          </Stack>

          <Stack className={classes.valveContainer}>
            <Stack className={classes.boxOne}>
              <Stack className={classes.typeSelect}>
                <SelectField
                  sx={{ width: '301px', borderRadius: '4px' }}
                  name="type"
                  label={t('commonLabelsType')}
                  form={form}
                  placeholder={t('formsPlaceholdersSelectOne')}
                  optionList={typeList}
                />
              </Stack>

              <Box className={classes.groupActive}>
                <Typography className={classes.textActive}>
                  {t('commonStatusActive')}
                </Typography>
                <AntSwitch
                  form={form}
                  name="active"
                  width={36}
                  height={19.2}
                  widthThumb={14.4}
                  heightThumb={14.4}
                  borderThumb={14.4}
                  borderTrack={19 / 2}
                  transform={'16'}
                />
              </Box>
            </Stack>

            <Stack>
              <InputLabelField
                name="priority"
                form={form}
                label={t('formsLabelsPriority')}
                placeholder={t('formsPlaceholdersTypeIn', {
                  field: t('formsLabelsPriority'),
                })}
                style={{
                  borderRadius: '4px',
                  height: 50,
                  width: { xs: '377px' },
                }}
                padding="8px 16px"
              />
            </Stack>

            <Stack>
              {/* <SelectField
                sx={{ width: '377px' }}
                name="addressValve"
                placeholder={t('formsPlaceholdersSelectOne')}
                label="IO"
                form={form}
                optionList={dataSelect}
                heightScroll="250px"
              /> */}
              <Select
                required
                minWidth={'301px'}
                filterFunc={false}
                label="IO"
                name="address"
                // bindLabel="label"
                bindKey="value"
                placeholder={t('formsPlaceholdersSelectOne')}
                form={form}
                height={'80px'}
                zIndex={1000}
                formatOptionLabel={(item: any) => (
                  <Stack spacing={0.3}>
                    <Stack fontWeight={600}>{item.name}</Stack>
                    {item.address && (
                      <Stack>
                        {item.address}-{item.index}
                      </Stack>
                    )}
                    {item.twiCustomName && <Stack>{item.twiCustomName}</Stack>}
                  </Stack>
                )}
                getOptions={getOptions()}
              />
            </Stack>

            <Stack>
              {/* <SelectField
                showClearButton={!isViewOnly}
                sx={{ width: '377px' }}
                name="flowSensorId"
                placeholder={t('formsPlaceholdersSelectOne')}
                label={t('flowSensor')}
                form={form}
                heightScroll="250px"
                optionList={
                  dataSensorOption?.map((item) => ({
                    label: item.name,
                    value: item.id,
                  })) || []
                }
              /> */}
              <Select
                minWidth={'301px'}
                filterFunc={false}
                label={t('flowSensor')}
                name="flowSensorId"
                // bindLabel="label"
                bindKey="id"
                placeholder={t('formsPlaceholdersSelectOne')}
                form={form}
                height={'80px'}
                zIndex={1000}
                isClearable
                formatOptionLabel={(item: any) => (
                  <Stack spacing={0.3}>
                    <Stack fontWeight={600}>{item.name}</Stack>
                    {item.address && (
                      <Stack>
                        {item.address}-{item.index}
                      </Stack>
                    )}
                    {item.twiCustomName && <Stack>{item.twiCustomName}</Stack>}
                  </Stack>
                )}
                getOptions={getFlowSensorOptions()}
              />
            </Stack>

            <InputLabelField
              name="startDelay"
              form={form}
              label={t('formsLabelsStartDelay(sec)')}
              placeholder={t('formsPlaceholdersTypeIn', {
                field: t('formsLabelsStartDelay(sec)'),
              })}
              style={{
                borderRadius: '4px',
                height: 50,
                width: { xs: '377px' },
              }}
              padding="8px 16px"
            />

            <InputLabelField
              name="stopDelay"
              form={form}
              label={t('formsLabelsStopDelay(sec)')}
              placeholder={t('formsPlaceholdersTypeIn', {
                field: t('formsLabelsStopDelay(sec)'),
              })}
              style={{
                borderRadius: '4px',
                height: 50,
                width: { xs: '377px' },
              }}
              padding="8px 16px"
            />

            <InputLabelField
              name="capacity"
              form={form}
              label={`${t('formsLabelsCapacity')} (${convertLabelUnit('l/m')})`}
              placeholder={t('formsPlaceholdersTypeIn', {
                field: t('formsLabelsCapacity'),
              })}
              style={{
                borderRadius: '4px',
                height: 50,
                width: { xs: '377px' },
              }}
              padding="8px 16px"
            />
          </Stack>
        </Stack>
      </form>

      {/* SCRUM-1032 QC item 6 — Cancel on a form with edits asks first. */}
      <Dialog open={isConfirmOpen} onClose={cancelClose}>
        <ConfirmModalUI
          title={t('formsUnsavedChangesTitle')}
          text={t('formsUnsavedChangesBody')}
          cancelText={t('formsActionsKeepEditing')}
          confirmText={t('formsActionsDiscardChanges')}
          onClose={cancelClose}
          onConfirm={confirmClose}
        />
      </Dialog>
    </Container>
  )
}

export default FormValveView
